// Radha — the public customer portal (Zoho Books client-portal style). No login:
// a token in the URL (from a staff-generated share link) is resolved server-side
// by the portal-statement Edge Function, which returns only that one customer's
// own invoices. Rendered before the auth gate in App.tsx, so a customer never
// sees or needs a Radha account.
import React, { useEffect, useState } from "react";

import { supabase } from "../lib/supabase";
import { upiPayLink } from "../lib/upi";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";
import { fmtMoney as inr, fmtDate } from "../lib/money";

interface PortalInvoice {
  id: string; invoice_number: string; taxable_amount: number; tax_amount: number; total_amount: number;
  status: string; issue_date: string; due_date: string;
}
interface PortalMessage {
  id: string; invoice_id: string | null; kind: "dispute" | "question" | "payment_proof";
  body: string; payment_reference: string | null; status: "open" | "resolved";
  staff_reply: string | null; replied_at: string | null; created_at: string;
}
interface PortalData {
  token_id: string; customer_name: string; tenant_legal_name: string | null; tenant_upi_vpa: string | null;
  payment_channel: "direct_pay" | "invoice_email" | "po_portal"; invoices: PortalInvoice[]; messages: PortalMessage[];
}

const KIND_LABEL: Record<PortalMessage["kind"], string> = {
  dispute: "Dispute a charge", question: "Ask a question", payment_proof: "Note a payment I've made",
};


function MessageForm({ token, invoices, onSent }: { token: string; invoices: PortalInvoice[]; onSent: () => void }) {
  const [kind, setKind] = useState<PortalMessage["kind"]>("question");
  const [invoiceId, setInvoiceId] = useState("");
  const [body, setBody] = useState("");
  const [paymentRef, setPaymentRef] = useState("");
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    setNote(null);
    const { data: d, error: e } = await supabase.functions.invoke("portal-message", {
      body: { token, kind, invoice_id: invoiceId || null, body, payment_reference: kind === "payment_proof" ? paymentRef : null },
    });
    setSending(false);
    if (e || d?.error) {
      const text = d?.error ?? (e ? await edgeFunctionErrorMessage(e) : "Couldn't send — please try again.");
      setNote({ ok: false, text });
      return;
    }
    setNote({ ok: true, text: "Sent" });
    setBody(""); setPaymentRef("");
    onSent();
  };

  return (
    <div style={{ border: "1px dashed var(--border-strong)", borderRadius: 10, padding: "14px 16px", marginTop: 24 }}>
      <div style={{ font: "600 14px Inter", marginBottom: 10 }}>Have a question or a charge to dispute?</div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <select value={kind} onChange={(e) => setKind(e.target.value as PortalMessage["kind"])}
          style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13 }}>
          {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
          style={{ padding: "6px 9px", border: "1px solid var(--border)", borderRadius: 7, fontSize: 13, minWidth: 180 }}>
          <option value="">General (no specific invoice)</option>
          {invoices.map((i) => <option key={i.id} value={i.id}>{i.invoice_number}</option>)}
        </select>
      </div>
      {kind === "payment_proof" && (
        <input value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} placeholder="UPI/UTR reference number"
          style={{ width: "100%", padding: "7px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, marginBottom: 8 }} />
      )}
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} maxLength={2000}
        placeholder={kind === "dispute" ? "Which charge, and why?" : kind === "payment_proof" ? "When did you pay, and how?" : "What would you like to ask?"}
        style={{ width: "100%", padding: "8px 10px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, resize: "vertical" }} />
      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={submit} disabled={sending || !body.trim()}
          style={{ padding: "7px 16px", background: "var(--accent-purple, #7C5CFC)", color: "#fff", border: 0, borderRadius: 8, cursor: "pointer", fontSize: 13, opacity: sending || !body.trim() ? 0.5 : 1 }}>
          {sending ? "Sending…" : "Send"}
        </button>
        {note && <span style={{ fontSize: 13, color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
      </div>
    </div>
  );
}

export default function PortalPage({ token }: { token: string }) {
  const [data, setData] = useState<PortalData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    supabase.functions.invoke("portal-statement", { body: { token } }).then(async ({ data: d, error: e }) => {
      if (e || d?.error) {
        const code = d?.error ?? (e ? await edgeFunctionErrorMessage(e) : null);
        setError(code === "EXPIRED_OR_INVALID" ? "This link has expired or is no longer valid." : "Couldn't load your statement.");
        return;
      }
      setData(d as PortalData);
    });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const box: React.CSSProperties = { maxWidth: 640, margin: "60px auto", padding: "0 20px", font: "14px Inter, system-ui" };
  const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500, textAlign: "left" };
  const td: React.CSSProperties = { padding: "9px 8px" };

  if (error) return <div style={box}><p style={{ color: "var(--status-critical)" }}>{error}</p></div>;
  if (!data) return <div style={box}><p style={{ color: "var(--fg-muted)" }}>Loading your statement…</p></div>;

  const due = data.invoices.reduce((s, i) => s + (i.status === "paid" ? 0 : i.total_amount), 0);

  return (
    <div style={box}>
      <div style={{ font: "600 18px Inter", marginBottom: 2 }}>{data.tenant_legal_name ?? "Statement"}</div>
      <div style={{ color: "var(--fg-muted)", marginBottom: 20 }}>Statement for {data.customer_name}</div>

      <div style={{ display: "flex", gap: 28, alignItems: "flex-end", marginBottom: 20 }}>
        <div>
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Balance due</div>
          <div style={{ font: "600 22px Inter", color: due > 0 ? "var(--status-critical)" : "var(--status-good)" }}>{inr(due)}</div>
        </div>
        {due > 0 && data.payment_channel === "direct_pay" && data.tenant_upi_vpa && (
          <a href={upiPayLink(data.tenant_upi_vpa, data.tenant_legal_name ?? "Payee", due, data.customer_name)}
            style={{ padding: "8px 16px", background: "var(--status-good)", color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 13 }}>
            Pay via UPI
          </a>
        )}
        {due > 0 && data.payment_channel === "po_portal" && (
          <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>Processed via your organisation's standard vendor-payment workflow.</span>
        )}
        {due > 0 && data.payment_channel === "invoice_email" && (
          <span style={{ fontSize: 12.5, color: "var(--fg-muted)" }}>Invoiced separately by email — please arrange payment per your usual process.</span>
        )}
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ color: "var(--fg-muted)", fontSize: 12 }}>
            <th style={th}>Invoice</th><th style={th}>Date</th><th style={th}>Due</th>
            <th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {data.invoices.map((i) => (
            <tr key={i.invoice_number} style={{ borderTop: "1px solid var(--border)" }}>
              <td style={td}>{i.invoice_number}</td>
              <td style={td}>{i.issue_date}</td>
              <td style={td}>{i.due_date}</td>
              <td style={{ ...td, textAlign: "right" }}>{inr(i.total_amount)}</td>
              <td style={{ ...td, color: i.status === "paid" ? "var(--status-good)" : "var(--fg-muted)" }}>{i.status}</td>
            </tr>
          ))}
          {data.invoices.length === 0 && <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={5}>No invoices on record.</td></tr>}
        </tbody>
      </table>

      <MessageForm token={token} invoices={data.invoices} onSent={load} />

      {data.messages.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ font: "600 14px Inter", marginBottom: 10 }}>Your messages</div>
          <div style={{ display: "grid", gap: 10 }}>
            {data.messages.map((m) => (
              <div key={m.id} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--fg-muted)", marginBottom: 4 }}>
                  <span>{KIND_LABEL[m.kind]}{m.invoice_id ? ` · ${data.invoices.find((i) => i.id === m.invoice_id)?.invoice_number ?? ""}` : ""}</span>
                  <span>{fmtDate(m.created_at)} · {m.status === "resolved" ? "resolved" : "open"}</span>
                </div>
                <div style={{ fontSize: 13 }}>{m.body}</div>
                {m.payment_reference && <div style={{ fontSize: 12, color: "var(--fg-muted)", marginTop: 2 }}>Ref: {m.payment_reference}</div>}
                {m.staff_reply && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)", fontSize: 13 }}>
                    <div style={{ fontSize: 11, color: "var(--fg-muted)", marginBottom: 2 }}>Reply</div>
                    {m.staff_reply}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
